import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Text, Icon, SearchBox, Stack, MessageBar, MessageBarType, ITextStyles, IRawStyle, TooltipHost, Callout, Checkbox, DirectionalHint, Spinner, SpinnerSize } from "@fluentui/react";
import { GridColDef, GridColumnVisibilityModel, GridRenderCellParams } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { CustomDataGrid } from "../../../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { getCustomerInitials } from "../../../../helper/CustomerInitials";
import { logEvent, logException } from "../../../../helper/AppInsightsHelper";
import {
  BaselineSecurityHealthTenantMetrics,
  useGetBaselineSecurityHealthRiskRollupQuery,
  useGetBaselineSecurityHealthTenantMetricsQuery,
  useGetAIReadyHealthTrendQuery,
  useGetAIReadyHealthCardMetricsQuery,
  AIReadyHealthFilter,
  AIReadyHealthTrendPoint,
} from "../../../../slices/BaselineSecurityHealth.slice";
import { useGetFavorableCustomerByLoginQuery, useInsertUpdateFavCustomersMutation } from "../../../../slices/ExploreCustomers.slice";
import {
  adoptionDistributionFromRiskBands,
  adoptionWoWFromTrend,
  applyInitiativeRiskFilter,
  matchesEngagementCohort,
  parseEngagementCohorts,
} from "./adoptionTrendUtils";

type RiskLevel = "High" | "Moderate" | "Low";

// =============================================================================
// Theme tokens — match Security360 palette + table typography
// =============================================================================
const FONT_BODY = 12;        // single body size used throughout the table
const FONT_LABEL = 11;       // small caption / labels
const FONT_HEADER = 12;      // grid header

const RISK_PALETTE: Record<RiskLevel, { color: string; bg: string; textColor: string }> = {
  High: { color: "#D77440", bg: "#FDF3EE", textColor: "#7D3B13" },
  Moderate: { color: "#D0B232", bg: "#FBF5D3", textColor: "#6B5B00" },
  Low: { color: "#73AA24", bg: "#EFF6E0", textColor: "#3B5E0F" },
};

// Adoption is the user-facing framing of the underlying risk band, and the two
// are inverses: High risk = Low Adoption, Moderate risk = Moderate Adoption,
// Low risk = High Adoption. Colors and icons stay keyed by the risk band so a
// Low Adoption tenant keeps the same "needs attention" styling as High risk.
const RISK_TO_ADOPTION: Record<RiskLevel, RiskLevel> = {
  High: "Low",
  Moderate: "Moderate",
  Low: "High",
};
const adoptionLabel = (risk: RiskLevel): string => `${RISK_TO_ADOPTION[risk]} Adoption`;

// Numeric rank used to sort the adoption columns by the *displayed* adoption
// level (High Adoption is highest) rather than the inverse internal risk band.
// Keyed by internal risk band: High risk = Low adoption (1) … Low risk = High
// adoption (3). Blank/unknown bands map to 0 so they group at one end.
const ADOPTION_RANK: Record<RiskLevel, number> = { High: 1, Moderate: 2, Low: 3 };
const adoptionSortValue = (band: RiskLevel | ""): number => (band ? ADOPTION_RANK[band] : 0);

// Initiatives whose per-tenant adoption band can be surfaced in the single risk
// column. "Overall" is the default; selecting another initiative swaps the
// column to that initiative's band — only one is ever shown at a time.
type Initiative = "Overall" | "Baseline" | "Passkey" | "Patching" | "Endpoint" | "Protection" | "Monitoring" | "Hygiene" | "DormantAccount" | "Vulnerabilities";
// Selectable set for the Initiatives filter. Intentional exclusions:
//  - Baseline: pre-existing exclusion — its adoption is already reflected in the
//    "Overall" band, so it isn't offered as a separate initiative.
//  - Hygiene ("Harden Delegated Consent and App Hygiene"), DormantAccount
//    ("Dormant and High Risk Accounts") and Vulnerabilities ("Code
//    Vulnerabilities Remediation"): temporarily hidden for now.
// The type, labels and mappers for all of these are kept, so any can be
// re-enabled simply by adding it back to this array.
const INITIATIVES: readonly Initiative[] = ["Overall", "Passkey", "Patching", "Endpoint", "Protection", "Monitoring"];
// Full category names as shown on the customer detail (Ready for AI) page, so
// the Initiatives dropdown and the grid column header read identically to the
// per-tenant panes the user sees when they drill in.
const INITIATIVE_LABEL: Record<Initiative, string> = {
  Overall: "Overall",
  Baseline: "Baseline",
  Passkey: "Phishing-resistant Authentication (Passkey)",
  Patching: "Patching: Stay on current software",
  Endpoint: "Manage and Secure Endpoints",
  Protection: "Email and Collaboration Protection",
  Monitoring: "Insider Risk and Data Monitoring",
  Hygiene: "Harden Delegated Consent and APP Hygiene",
  DormantAccount: "Dormant and High Risk Accounts",
  Vulnerabilities: "Code Vulnerabilities Remediation",
};
// Grid header for the (single) adoption column, keyed by the selected initiative.
const INITIATIVE_COLUMN_HEADER: Record<Initiative, string> = {
  Overall: "Overall Adoption",
  Baseline: "Baseline",
  Passkey: "Phishing-resistant Authentication (Passkey)",
  Patching: "Patching: Stay on current software",
  Endpoint: "Manage and Secure Endpoints",
  Protection: "Email and Collaboration Protection",
  Monitoring: "Insider Risk and Data Monitoring",
  Hygiene: "Harden Delegated Consent and APP Hygiene",
  DormantAccount: "Dormant and High Risk Accounts",
  Vulnerabilities: "Code Vulnerabilities Remediation",
};

// Parses a signed change value string (e.g. "+14", "+0", "-3") to a number.
const parseChange = (raw: string): number => {
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
};

// Formats an ISO date pair into a compact "MMM d – MMM d" range for the MoM/WoW
// cells. Returns whichever bound is available, or "" when neither parses.
const formatDateRange = (start: string | null, end: string | null): string => {
  const fmt = (iso: string | null): string => {
    if (!iso) return "";
    // These are date-only comparison-window boundaries stored at UTC midnight.
    // Format from the YYYY-MM-DD portion in UTC so the browser's local timezone
    // can't shift a UTC-midnight value into the previous day.
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return "";
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).format(d);
  };
  const s = fmt(start);
  const e = fmt(end);
  if (s && e) return `${s} – ${e}`;
  return s || e;
};

const formatSnapshotDate = (iso: string | null): string => formatDateRange(iso, null);

// Threshold copy shown in the Info tooltip on each KPI tile. Overall Adoption is
// the Composite score of BSM Settings Index and Wave 1 recommendations index.
const ADOPTION_DESCRIPTIONS: Record<RiskLevel, string> = {
  Low: "High Adoption: This indicates the overall adoption level of a tenant based on the BSM Settings Index and Wave 1 Recommendations Index. A tenant is classified as High Adoption when both indices have an adoption score of 80% or higher.",
  Moderate: "Moderate Adoption: This indicates the overall adoption level of a tenant based on the BSM Settings Index and Wave 1 Recommendations Index. A tenant is classified as Moderate Adoption when neither index is below 50% and at least one index has an adoption score between 50% and less than 80%.",
  High: "Low Adoption: This indicates the overall adoption level of a tenant based on the BSM Settings Index and Wave 1 Recommendations Index. If either index has an adoption score below 50%, the tenant is classified as Low Adoption.",
};

type RiskFilter = "all" | "High" | "Moderate" | "Low";

const S500_OPTIONS = ["S500", "Non-S500"];

// Sentinel value used in the License SKU filter dropdown to represent tenants
// whose parsed SKU list is empty (no E3 / E5 / E7 / other SKU). Safe to reuse
// the literal "None" label because splitCsv strips it from real SKU lists.
const LICENSE_SKU_NONE = "None";

// Display-only label mapping for the M365 License SKU filter. The API still
// returns the bare "E3" / "E5" / "E7" values (and the "None" sentinel); we only
// relabel for the UI so filter matching keeps working against the raw values.
const formatLicenseSku = (sku: string): string => {
  switch (sku) {
    case "E3": return "M365 E3";
    case "E5": return "M365 E5";
    case "E7": return "M365 E7";
    case LICENSE_SKU_NONE: return "None of these";
    default: return sku;
  }
};

const M365_LICENSE_SKU_TOOLTIP =
  "Regarding the M365 license SKUs, this filter includes only Microsoft 365 E3, Microsoft 365 E5, and Microsoft 365 E7 licenses. Other license types, such as EMS *, E5 S&C, Office 365 *, or add-on subscriptions, are not included. For a complete view of all licenses and subscriptions assigned to the customer, please refer to the License and Subscription tab on the customer page.";

// Toggle a value in a Set immutably.
function toggleInSet<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

// =============================================================================
// Sub-components
// =============================================================================
const KpiWoWIndicator: React.FC<{
  value: number | null;
  percentage: number | null;
  start: string | null;
  end: string | null;
  maximumFractionDigits?: number;
  unit?: string;
  label?: string;
  tooltipText?: string;
}> = ({ value, percentage, start, end, maximumFractionDigits = 0, unit = "tenants", label = "WoW", tooltipText }) => {
  const normalizedValue = value !== null && Object.is(value, -0) ? 0 : value;
  const normalizedPercentage = percentage !== null && Object.is(percentage, -0) ? 0 : percentage;
  const isPositive = normalizedValue !== null && normalizedValue > 0;
  const isNegative = normalizedValue !== null && normalizedValue < 0;
  const color = normalizedValue === null
    ? "#8A8886"
    : isPositive
      ? "#107C41"
      : isNegative
        ? "#C4314B"
        : "#605E5C";
  const backgroundColor = normalizedValue === null
    ? "#F3F2F1"
    : isPositive
      ? "#EFF6EC"
      : isNegative
        ? "#FDE7E9"
        : "#F3F2F1";
  const formattedValue = normalizedValue === null
    ? "—"
    : `${isPositive ? "+" : isNegative ? "-" : ""}${Math.abs(normalizedValue).toLocaleString("en-US", { maximumFractionDigits })}`;
  const formattedPercentage = normalizedPercentage === null
    ? null
    : `${normalizedPercentage > 0 ? "+" : normalizedPercentage < 0 ? "-" : ""}${Math.abs(normalizedPercentage).toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
  const range = formatDateRange(start, end);
  const tooltip = tooltipText ?? (normalizedValue === null
    ? "Week-over-week comparison is unavailable."
    : `${formattedValue} ${unit}${formattedPercentage ? ` (${formattedPercentage})` : ""} compared with the previous weekly snapshot${range ? ` (${range})` : ""}.`);

  return (
    <TooltipHost content={tooltip} directionalHint={DirectionalHint.bottomCenter}>
      <span
        aria-label={tooltip}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          padding: "2px 7px",
          borderRadius: 4,
          backgroundColor,
          color,
          fontSize: 12,
          fontWeight: 700,
          lineHeight: "16px",
          whiteSpace: "nowrap" as const,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {isPositive && <Icon iconName="CaretSolidUp" styles={{ root: { fontSize: 10, color } }} />}
        {isNegative && <Icon iconName="CaretSolidDown" styles={{ root: { fontSize: 10, color } }} />}
        {formattedValue}{formattedPercentage ? ` (${formattedPercentage})` : ""} {label}
      </span>
    </TooltipHost>
  );
};

const KpiCardHeader: React.FC<{
  title: string;
  iconName: string;
  accentColor: string;
  background: string;
  borderColor: string;
  infoText: string;
  infoAriaLabel: string;
}> = ({ title, iconName, accentColor, background, borderColor, infoText, infoAriaLabel }) => (
  <div style={{
    background,
    height: 60,
    boxSizing: "border-box",
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    gap: 9,
    borderBottom: `1px solid ${borderColor}`,
  }}>
    <div style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 30,
      height: 30,
      borderRadius: "50%",
      backgroundColor: "#FFFFFF",
      boxShadow: `0 0 0 1px ${accentColor}44`,
      flexShrink: 0,
    }}>
      <Icon iconName={iconName} styles={{ root: { color: accentColor, fontSize: 15 } }} />
    </div>
    <span style={{
      color: accentColor,
      display: "-webkit-box",
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      WebkitBoxOrient: "vertical" as const,
      WebkitLineClamp: 2,
      fontSize: 18,
      fontWeight: 700,
      lineHeight: "20px",
      fontFamily: "'Segoe UI', sans-serif",
    }}>
      {title}
    </span>
    <TooltipHost content={infoText} directionalHint={DirectionalHint.bottomCenter}>
      <span
        role="img"
        aria-label={infoAriaLabel}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") event.stopPropagation(); }}
        tabIndex={0}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 18,
          height: 18,
          borderRadius: "50%",
          cursor: "help",
          color: accentColor,
          flexShrink: 0,
        }}
      >
        <Icon iconName="Info" styles={{ root: { fontSize: 13, color: accentColor } }} />
      </span>
    </TooltipHost>
  </div>
);

const KpiComparisonRow: React.FC<{
  wowIndicator: React.ReactNode;
  statusIndicator?: React.ReactNode;
}> = ({ wowIndicator, statusIndicator }) => (
  <div style={{ minHeight: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, overflow: "hidden" }}>
    <span style={{ display: "inline-flex", minWidth: 0 }}>{statusIndicator}</span>
    <span style={{ display: "inline-flex", minWidth: 0, marginLeft: "auto" }}>{wowIndicator}</span>
  </div>
);

const SummaryKpiCard: React.FC<{
  label: string; pct: number; count: number; total: number;
  wow: number | null; wowPercentage: number | null; wowStart: string | null; wowEnd: string | null;
  riskLevel: RiskLevel; active: boolean; isLoading: boolean; onClick: () => void;
}> = ({ label, pct, count, total, wow, wowPercentage, wowStart, wowEnd, riskLevel, active, isLoading, onClick }) => {
  const { color, bg } = RISK_PALETTE[riskLevel];
  const iconName = riskLevel === "High" ? "AlertSolid" : riskLevel === "Moderate" ? "WarningSolid" : "SkypeCircleCheck";

  return (
    <div
      role="button"
      tabIndex={0}
      aria-busy={isLoading}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      style={{
        flex: 1, minWidth: 260,
        display: "flex",
        flexDirection: "column",
        border: active ? `1.5px solid ${color}` : "1px solid #EDEBE9",
        borderRadius: 10,
        backgroundColor: "#FFFFFF",
        cursor: "pointer",
        textAlign: "left" as const,
        boxShadow: active ? `0 2px 10px ${color}33` : "0 1px 3px rgba(16,24,40,.08)",
        transition: "transform 0.15s, box-shadow 0.15s, border-color 0.15s",
        outline: "none",
        userSelect: "none" as const,
        position: "relative" as const,
        overflow: "hidden" as const,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 6px 18px ${color}40`;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = active ? `0 2px 10px ${color}33` : "0 1px 3px rgba(16,24,40,.08)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <KpiCardHeader
        title={label}
        iconName={iconName}
        accentColor={color}
        background={bg}
        borderColor={`${color}22`}
        infoText={ADOPTION_DESCRIPTIONS[riskLevel]}
        infoAriaLabel={`${label} threshold`}
      />

      {/* Body: metric row + progress bar */}
      {isLoading ? (
        <div style={{ height: 82, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spinner size={SpinnerSize.small} />
        </div>
      ) : (
        <div style={{ padding: "9px 12px 10px", display: "flex", flexDirection: "column", gap: 7 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 5, minWidth: 0 }}>
              <span style={{
                fontSize: 19, fontWeight: 700, color: "#1A1A2E",
                lineHeight: 1,
                fontFamily: "Roboto, 'Segoe UI', sans-serif",
                fontVariantNumeric: "tabular-nums",
              }}>
                {count}
              </span>
              <span style={{
                fontSize: 11, color: "#8A8886", fontWeight: 500,
                fontFamily: "'Segoe UI', sans-serif",
                whiteSpace: "nowrap" as const,
              }}>
                of {total} tenants
              </span>
            </div>
            <span style={{
              fontSize: 12, fontWeight: 700, color,
              fontFamily: "Roboto, 'Segoe UI', sans-serif",
              fontVariantNumeric: "tabular-nums",
              backgroundColor: bg,
              padding: "1px 7px",
              borderRadius: 999,
              lineHeight: "15px",
            }}>
              {pct}%
            </span>
          </div>
          <div style={{ height: 4, borderRadius: 2, backgroundColor: "#F3F2F1", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, backgroundColor: color, borderRadius: 2, transition: "width 0.3s" }} />
          </div>
          <KpiComparisonRow
            wowIndicator={<KpiWoWIndicator value={wow} percentage={wowPercentage} start={wowStart} end={wowEnd} />}
            statusIndicator={active ? (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 3,
                backgroundColor: color, color: "#FFFFFF",
                padding: "1px 6px", borderRadius: 4,
                fontSize: 9, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}>
                <Icon iconName="Filter" styles={{ root: { fontSize: 9, color: "#FFFFFF" } }} />
                Filtered
              </span>
            ) : null}
          />
        </div>
      )}

    </div>
  );
};

/**
 * AvgActionsKpiCard: displays the average actions per tenant metric
 * with a matching design to other KPI cards.
 * Shows the value returned by Kusto function AvgActionperTenant.
 */
const AvgActionsKpiCard: React.FC<{
  avgActions: number;
}> = ({ avgActions }) => {
  const accentColor = "#0078D4";
  const bgGradient = "linear-gradient(135deg, #EFF6FC 0%, #E3F1FB 100%)";

  return (
    <div
      style={{
        flex: 1, minWidth: 0,
        display: "flex",
        flexDirection: "column",
        border: "1px solid #C7E0F4",
        borderRadius: 10,
        backgroundColor: "#FFFFFF",
        boxShadow: "0 1px 3px rgba(16,24,40,.08)",
        overflow: "hidden" as const,
        position: "relative" as const,
      }}
    >
      {/* Hero band with gradient - matching other cards */}
      <div style={{
        background: bgGradient,
        padding: "10px 14px 8px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        borderBottom: "1px solid #C7E0F422",
      }}>
        <div style={{
          display: "inline-flex",
          alignItems: "center", justifyContent: "center",
          width: 28, height: 28,
          borderRadius: "50%",
          backgroundColor: "#FFFFFF",
          boxShadow: `0 0 0 1px ${accentColor}44`,
          flexShrink: 0,
        }}>
          <Icon iconName="BarChartVerticalFill" styles={{ root: { color: accentColor, fontSize: 14 } }} />
        </div>
        <span style={{
          fontSize: 17, fontWeight: 700, color: accentColor,
          letterSpacing: "-0.01em",
          lineHeight: 1.15,
          fontFamily: "'Segoe UI', sans-serif",
          flex: 1, minWidth: 0,
        }}>
          Avg Actions / Tenant
        </span>
        <TooltipHost
          content="Average number of security actions per tenant as returned by the Kusto function."
          directionalHint={DirectionalHint.bottomCenter}
        >
          <span
            role="img"
            aria-label="Avg Actions info"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 18,
              height: 18,
              borderRadius: "50%",
              cursor: "help",
              color: accentColor,
            }}
          >
            <Icon iconName="Info" styles={{ root: { fontSize: 13, color: accentColor } }} />
          </span>
        </TooltipHost>
      </div>

      {/* Body: show the value from Kusto */}
      <div style={{ padding: "8px 14px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{
            fontSize: 24, fontWeight: 700, color: "#1A1A2E",
            lineHeight: 1,
            fontFamily: "Roboto, 'Segoe UI', sans-serif",
            fontVariantNumeric: "tabular-nums",
          }}>
            {avgActions.toFixed(2)}
          </span>
          <span style={{
            fontSize: 11, color: "#8A8886", fontWeight: 500,
            fontFamily: "'Segoe UI', sans-serif",
          }}>
            actions/tenant
          </span>
        </div>
        <div style={{ height: 3, borderRadius: 2, backgroundColor: "#F3F2F1", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${Math.min(100, avgActions)}%`, backgroundColor: accentColor, borderRadius: 2, transition: "width 0.3s" }} />
        </div>
      </div>
    </div>
  );
};

/**
 * TotalControlsAdoptedCard: total security controls adopted across the in-scope
 * tenants, framed as "of {total} controls" with the average controls/tenant.
 */
const TotalControlsAdoptedCard: React.FC<{
  adopted: number; total: number; avgPerTenant: number; wow: number | null; wowPercentage: number | null;
  cumulativeStartDate: string | null; wowStart: string | null; wowEnd: string | null; isLoading: boolean;
}> = ({ adopted, total, avgPerTenant, wow, wowPercentage, cumulativeStartDate, wowStart, wowEnd, isLoading }) => {
  const accentColor = "#0078D4";
  const bgGradient = "linear-gradient(135deg, #EFF6FC 0%, #E3F1FB 100%)";
  const pct = total > 0 ? Math.round((adopted / total) * 100) : 0;
  const cumulativeSince = formatSnapshotDate(cumulativeStartDate);

  return (
    <div aria-busy={isLoading} style={{
      flex: 1, minWidth: 260, display: "flex", flexDirection: "column",
      border: "1px solid #C7E0F4", borderRadius: 10, backgroundColor: "#FFFFFF",
      boxShadow: "0 1px 3px rgba(16,24,40,.08)", overflow: "hidden" as const, position: "relative" as const,
    }}>
      <KpiCardHeader
        title="Total Controls Adopted"
        iconName="Completed"
        accentColor={accentColor}
        background={bgGradient}
        borderColor="#C7E0F422"
        infoText="Number of 'Ready for AI' controls moved from red to green thus far since beginning of FY27 (June 29th 2026)."
        infoAriaLabel="Total Controls Adopted info"
      />
      {isLoading ? (
        <div style={{ height: 82, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spinner size={SpinnerSize.small} />
        </div>
      ) : (
        <div style={{ padding: "9px 12px 10px", display: "flex", flexDirection: "column", gap: 7 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 5, minWidth: 0 }}>
              <span style={{ fontSize: 19, fontWeight: 700, color: "#1A1A2E", lineHeight: 1, fontFamily: "Roboto, 'Segoe UI', sans-serif", fontVariantNumeric: "tabular-nums" }}>
                {adopted.toLocaleString()}
              </span>
              <span
                title={cumulativeSince ? `Cumulative since ${cumulativeSince}` : undefined}
                style={{ fontSize: 11, color: "#8A8886", fontWeight: 500, fontFamily: "'Segoe UI', sans-serif", whiteSpace: "nowrap" as const }}
              >
                of {total.toLocaleString()} controls{cumulativeSince ? ` · since ${cumulativeSince}` : ""}
              </span>
            </div>
            <span style={{ fontSize: 11, color: "#605E5C", fontWeight: 600, fontFamily: "Roboto, 'Segoe UI', sans-serif", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" as const }}>
              {avgPerTenant.toFixed(1)}/tenant
            </span>
          </div>
          <div style={{ height: 4, borderRadius: 2, backgroundColor: "#F3F2F1", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, backgroundColor: accentColor, borderRadius: 2, transition: "width 0.3s" }} />
          </div>
          <KpiComparisonRow wowIndicator={(
            <KpiWoWIndicator
              value={wow}
              percentage={wowPercentage}
              start={wowStart}
              end={wowEnd}
              unit="controls adopted"
            />
          )} />
        </div>
      )}
    </div>
  );
};

/** Cumulative and weekly tenant adoption improvements. */
const TenantsImprovedCard: React.FC<{
  cumulativeImproved: number | null;
  improvedThisWeek: number;
  improvedPreviousWeek: number;
  improvedWoW: number | null;
  improvedWoWPercentage: number | null;
  total: number;
  cumulativeStartDate: string | null;
  weekStartDate: string | null;
  weekEndDate: string | null;
  isLoading: boolean;
}> = ({ cumulativeImproved, improvedThisWeek, improvedPreviousWeek, improvedWoW, improvedWoWPercentage, total, cumulativeStartDate, weekStartDate, weekEndDate, isLoading }) => {
  const accentColor = "#107C10";
  const bgGradient = "linear-gradient(135deg, #EFF6EC 0%, #DFF3D8 100%)";
  const hasCumulativeMetric = cumulativeImproved !== null;
  const primaryValue = cumulativeImproved ?? improvedThisWeek;
  const pct = total > 0 ? Math.round((primaryValue / total) * 100) : 0;
  const sinceLabel = formatSnapshotDate(cumulativeStartDate);

  return (
    <div aria-busy={isLoading} style={{
      flex: 1, minWidth: 260, display: "flex", flexDirection: "column",
      border: "1px solid #C6E6C6", borderRadius: 10, backgroundColor: "#FFFFFF",
      boxShadow: "0 1px 3px rgba(16,24,40,.08)", overflow: "hidden" as const, position: "relative" as const,
    }}>
      <KpiCardHeader
        title="Tenants Improved Adoption"
        iconName="Trending12"
        accentColor={accentColor}
        background={bgGradient}
        borderColor="#C6E6C622"
        infoText="Number of tenants that improved adoption state since beginning of FY27 (June 29th 2026). Note: A tenant moving from Low to High is still 1 tenant and vice versa."
        infoAriaLabel="Tenants Improved Adoption info"
      />
      {isLoading ? (
        <div style={{ height: 82, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spinner size={SpinnerSize.small} />
        </div>
      ) : (
        <div style={{ padding: "9px 12px 10px", display: "flex", flexDirection: "column", gap: 7 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 20 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 5, minWidth: 0, overflow: "hidden", whiteSpace: "nowrap" as const }}>
              <span style={{ fontSize: 19, fontWeight: 700, color: "#1A1A2E", lineHeight: 1, fontFamily: "Roboto, 'Segoe UI', sans-serif", fontVariantNumeric: "tabular-nums" }}>
                {primaryValue.toLocaleString()}
              </span>
              <span style={{ fontSize: 11, color: "#8A8886", fontWeight: 500, fontFamily: "'Segoe UI', sans-serif", whiteSpace: "nowrap" as const }}>
                {hasCumulativeMetric
                  ? `cumulative${sinceLabel ? ` since ${sinceLabel}` : ""}`
                  : `of ${total.toLocaleString()} tenants`}
              </span>
              {hasCumulativeMetric && (
                <span style={{ fontSize: 11, color: "#605E5C", fontWeight: 600, fontFamily: "'Segoe UI', sans-serif", whiteSpace: "nowrap" as const }}>
                  · {improvedThisWeek >= 0 ? "+" : ""}{improvedThisWeek.toLocaleString()} this week
                </span>
              )}
            </div>
          </div>
          <div style={{ height: 4, borderRadius: 2, backgroundColor: "#F3F2F1", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, backgroundColor: accentColor, borderRadius: 2, transition: "width 0.3s" }} />
          </div>
          <KpiComparisonRow wowIndicator={hasCumulativeMetric ? (
            <KpiWoWIndicator
              value={improvedWoW}
              percentage={improvedWoWPercentage}
              start={weekStartDate}
              end={weekEndDate}
              unit="tenant improvements"
              tooltipText={`Weekly improvements changed by ${improvedWoW !== null && improvedWoW >= 0 ? "+" : ""}${(improvedWoW ?? 0).toLocaleString()} from ${improvedPreviousWeek.toLocaleString()} in the prior week${formatDateRange(weekStartDate, weekEndDate) ? ` (${formatDateRange(weekStartDate, weekEndDate)})` : ""}.`}
            />
          ) : null} />
        </div>
      )}
    </div>
  );
};

// Adoption bands drawn on the trend chart, keyed by the direct (non-inverted)
// count columns returned by the trend function. Colors match RISK_PALETTE.
const ADOPTION_TREND_SERIES: ReadonlyArray<{ key: "low" | "moderate" | "high"; color: string; label: string }> = [
  { key: "high", color: "#73AA24", label: "High Adoption" },
  { key: "moderate", color: "#D0B232", label: "Moderate Adoption" },
  { key: "low", color: "#D77440", label: "Low Adoption" },
];

/**
 * AdoptionTrendChart: an SVG line chart showing the weekly time-trend of the
 * three adoption bands (High / Moderate / Low) for the selected initiative.
 * Driven entirely by real, server-filtered trend points.
 */
const AdoptionTrendChart: React.FC<{
  points: AIReadyHealthTrendPoint[];
  initiativeLabel: string;
  isLoading: boolean;
  error?: string | null;
  totalPointsFetched?: number;
}> = ({ points, initiativeLabel, isLoading, error, totalPointsFetched }) => {
  const sorted = useMemo(
    () => [...points].sort((a, b) => (a.factDate ?? "").localeCompare(b.factDate ?? "")),
    [points]
  );
  const visibleSeries = ADOPTION_TREND_SERIES.filter((series) =>
    sorted.some((point) => point[series.key] !== 0)
  );

  const formatDate = (iso: string | null): string => {
    if (!iso) return "";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return "";
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).format(d);
  };

  const chartHeader = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Icon iconName="LineChart" styles={{ root: { fontSize: 14, color: "#5C2E91" } }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: "#323130", fontFamily: "'Segoe UI', sans-serif" }}>
          Adoption Trend
        </span>
        <span style={{ fontSize: 11, color: "#8A8886", fontWeight: 500 }}>· {initiativeLabel}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        {visibleSeries.map((s) => (
          <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "#605E5C", fontWeight: 600 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: s.color, display: "inline-block" }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );

  const cardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF", border: "1px solid #EDEBE9", borderRadius: 8,
    padding: "12px 18px", boxShadow: "0 1px 3px rgba(16,24,40,.06)",
  };

  if (isLoading) {
    return (
      <div style={cardStyle} aria-busy>
        {chartHeader}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 64 }}>
          <Spinner label="Loading..." size={SpinnerSize.medium} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={cardStyle}>
        {chartHeader}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 64, color: "#A4262C", fontSize: 12, padding: "0 12px", textAlign: "center" as const }}>
          Failed to load adoption trend: {error}
        </div>
      </div>
    );
  }

  if (sorted.length === 0) {
    // Disambiguate between "no data for this initiative" vs "no data returned at all"
    const msg = (totalPointsFetched ?? 0) > 0
      ? `No adoption trend data for ${initiativeLabel}.`
      : "No adoption trend data available for the current filters.";
    return (
      <div style={cardStyle}>
        {chartHeader}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 64, color: "#8A8886", fontSize: 13 }}>
          {msg}
        </div>
      </div>
    );
  }

  const W = 720, H = 64, padL = 40, padR = 16, padT = 6, padB = 18;
  const pw = W - padL - padR, ph = H - padT - padB;
  const N = sorted.length;
  const maxVal = Math.max(
    4,
    ...sorted.map((p) => Math.max(p.low, p.moderate, p.high))
  );
  const X = (i: number): number => padL + (N === 1 ? pw / 2 : (pw * i) / (N - 1));
  const Y = (v: number): number => padT + ph * (1 - v / maxVal);

  const ticks = Array.from(new Set([0, Math.round(maxVal / 2), maxVal]));
  const labels = sorted.map((p) => formatDate(p.factDate));

  return (
    <div style={cardStyle}>
      {chartHeader}
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Adoption trend over ${N} snapshots for ${initiativeLabel}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {ticks.map((v) => {
          const y = Y(v);
          return (
            <g key={`grid-${v}`}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#EDEBE9" strokeWidth={1} />
              <text x={padL - 6} y={y + 3} textAnchor="end" style={{ fontSize: 7, fill: "#8A8886" }}>{v}</text>
            </g>
          );
        })}
        {labels.map((lb, i) => (
          <text key={`xl-${i}`} x={X(i)} y={H - 8} textAnchor="middle" style={{ fontSize: 7, fill: "#8A8886" }}>{lb}</text>
        ))}
        {visibleSeries.map((s) => {
          const d = sorted.map((p, i) => `${i === 0 ? "M" : "L"}${X(i).toFixed(1)} ${Y(p[s.key]).toFixed(1)}`).join(" ");
          return (
            <g key={`series-${s.key}`}>
              <path d={d} fill="none" stroke={s.color} strokeWidth={1} strokeLinecap="round" strokeLinejoin="round" />
              {sorted.map((p, i) => (
                <circle key={`dot-${s.key}-${i}`} cx={X(i)} cy={Y(p[s.key])} r={2.6} fill={s.color}>
                  <title>{`${labels[i]}: ${p[s.key].toLocaleString()} tenants (${s.label})`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const FilterPill: React.FC<{ label: string; active: boolean; activeColor?: string; onClick: () => void; icon?: string }> = ({
  label, active, activeColor = "#0078D4", onClick, icon,
}) => (
  <button
    onClick={onClick}
    style={{
      padding: "5px 14px",
      borderRadius: 999,
      border: active ? `1.5px solid ${activeColor}` : "1px solid #C8C6C4",
      backgroundColor: active ? activeColor : "#FFFFFF",
      color: active ? "#FFFFFF" : "#323130",
      fontSize: FONT_BODY,
      fontWeight: active ? 600 : 500,
      cursor: "pointer",
      lineHeight: "20px",
      transition: "all 0.15s",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    }}
  >
    {icon ? <Icon iconName={icon} styles={{ root: { fontSize: 11, color: active ? "#FFFFFF" : activeColor } }} /> : null}
    {label}
  </button>
);

/**
 * ClearFiltersButton: pill-styled "Clear all" that resets every filter to its
 * default. Dimmed when nothing is active; badge shows the active count.
 */
const ClearFiltersButton: React.FC<{ activeCount: number; onClick: () => void }> = ({ activeCount, onClick }) => {
  const active = activeCount > 0;
  return (
    <button
      onClick={onClick}
      disabled={!active}
      title={active ? "Reset all filters" : "No filters applied"}
      style={{
        padding: "5px 12px",
        borderRadius: 999,
        border: active ? "1.5px solid #A4262C" : "1px dashed #C8C6C4",
        backgroundColor: active ? "#FDECF0" : "#FFFFFF",
        color: active ? "#A4262C" : "#8A8886",
        fontSize: FONT_BODY,
        fontWeight: active ? 600 : 500,
        cursor: active ? "pointer" : "default",
        lineHeight: "20px",
        transition: "all 0.15s",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <Icon iconName="ClearFilter" styles={{ root: { fontSize: 12, color: active ? "#A4262C" : "#8A8886" } }} />
      Clear all
      {active && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 18,
            height: 18,
            padding: "0 5px",
            borderRadius: 999,
            backgroundColor: "#A4262C",
            color: "#FFFFFF",
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {activeCount}
        </span>
      )}
    </button>
  );
};

/**
 * DropdownFilterPill: a pill-shaped button that opens a Callout with a
 * scrollable checkbox list for multi-select filtering. Shows the selection
 * summary ("All", a single value, or "N selected") inline.
 */
const DropdownFilterPill: React.FC<{
  label: string;
  options: readonly string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  accentColor?: string;
  // Maps a raw option value to the label shown to the user. Lets the filter keep
  // matching on the raw data value (e.g. "High") while displaying an adoption
  // label (e.g. "Low Adoption").
  formatOption?: (opt: string) => string;
  // Optional info tooltip surfaced via an (i) icon next to the pill label.
  tooltip?: string;
}> = ({ label, options, selected, onChange, accentColor = "#0078D4", formatOption, tooltip }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const active = selected.size > 0;
  const display = (opt: string) => (formatOption ? formatOption(opt) : opt);
  const summary =
    selected.size === 0 ? "All"
      : selected.size === 1 ? display(Array.from(selected)[0])
        : `${selected.size} selected`;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "5px 10px 5px 12px",
          borderRadius: 999,
          border: active ? `1.5px solid ${accentColor}` : "1px solid #C8C6C4",
          backgroundColor: active ? `${accentColor}14` : "#FFFFFF",
          color: active ? accentColor : "#323130",
          fontSize: FONT_BODY,
          fontWeight: 600,
          cursor: "pointer",
          lineHeight: "20px",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          maxWidth: 280,
          transition: "all 0.15s",
        }}
      >
        <span style={{ color: active ? accentColor : "#605E5C", fontWeight: 500 }}>{label}:</span>
        {tooltip && (
          <TooltipHost content={tooltip} directionalHint={DirectionalHint.bottomCenter} styles={{ root: { display: "inline-flex" } }}>
            <Icon
              iconName="Info"
              aria-label={`About ${label}`}
              onClick={(e) => e.stopPropagation()}
              styles={{ root: { fontSize: 12, color: active ? accentColor : "#605E5C", cursor: "help" } }}
            />
          </TooltipHost>
        )}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>
          {summary}
        </span>
        <Icon iconName="ChevronDown" styles={{ root: { fontSize: 10, color: active ? accentColor : "#605E5C" } }} />
      </button>
      {open && buttonRef.current && (
        <Callout
          target={buttonRef.current}
          onDismiss={() => setOpen(false)}
          directionalHint={DirectionalHint.bottomLeftEdge}
          isBeakVisible={false}
          gapSpace={4}
          setInitialFocus
        >
          <div style={{ padding: 10, minWidth: 240, maxWidth: 340 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" styles={{ root: { paddingBottom: 6, borderBottom: "1px solid #F3F2F1", marginBottom: 6 } }}>
              <span style={{ fontSize: FONT_LABEL, fontWeight: 700, color: "#605E5C", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
              {active && (
                <button
                  onClick={() => onChange(new Set())}
                  style={{ background: "none", border: "none", color: accentColor, cursor: "pointer", fontSize: FONT_LABEL, fontWeight: 600, padding: 0 }}
                >
                  Clear
                </button>
              )}
            </Stack>
            <div style={{ maxHeight: 260, overflowY: "auto" }}>
              {options.map((opt) => (
                <Checkbox
                  key={opt}
                  label={display(opt)}
                  checked={selected.has(opt)}
                  onChange={() => onChange(toggleInSet(selected, opt))}
                  styles={{ root: { padding: "5px 2px" }, text: { fontSize: FONT_BODY } }}
                />
              ))}
            </div>
          </div>
        </Callout>
      )}
    </>
  );
};

/**
 * SingleSelectFilterPill: same pill styling as DropdownFilterPill but for a
 * single-choice selector (exactly one value is always selected). Selecting an
 * option replaces the current value and closes the callout. Used by the
 * Initiatives selector, which swaps the single adoption column shown in the grid.
 */
function SingleSelectFilterPill<T extends string>({
  label, options, selected, onChange, accentColor = "#0078D4", formatOption, defaultValue,
}: {
  label: string;
  options: readonly T[];
  selected: T;
  onChange: (next: T) => void;
  accentColor?: string;
  formatOption?: (opt: T) => string;
  // When set, the pill renders in the neutral (non-accented) state while the
  // selection equals this value (e.g. the default "Overall").
  defaultValue?: T;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const display = (opt: T) => (formatOption ? formatOption(opt) : opt);
  const active = defaultValue === undefined ? true : selected !== defaultValue;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "5px 10px 5px 12px",
          borderRadius: 999,
          border: active ? `1.5px solid ${accentColor}` : "1px solid #C8C6C4",
          backgroundColor: active ? `${accentColor}14` : "#FFFFFF",
          color: active ? accentColor : "#323130",
          fontSize: FONT_BODY,
          fontWeight: 600,
          cursor: "pointer",
          lineHeight: "20px",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          maxWidth: 280,
          transition: "all 0.15s",
        }}
      >
        <span style={{ color: active ? accentColor : "#605E5C", fontWeight: 500 }}>{label}:</span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>
          {display(selected)}
        </span>
        <Icon iconName="ChevronDown" styles={{ root: { fontSize: 10, color: active ? accentColor : "#605E5C" } }} />
      </button>
      {open && buttonRef.current && (
        <Callout
          target={buttonRef.current}
          onDismiss={() => setOpen(false)}
          directionalHint={DirectionalHint.bottomLeftEdge}
          isBeakVisible={false}
          gapSpace={4}
          setInitialFocus
        >
          <div style={{ padding: 10, minWidth: 200, maxWidth: 300 }}>
            <Stack styles={{ root: { paddingBottom: 6, borderBottom: "1px solid #F3F2F1", marginBottom: 6 } }}>
              <span style={{ fontSize: FONT_LABEL, fontWeight: 700, color: "#605E5C", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
            </Stack>
            <div style={{ maxHeight: 260, overflowY: "auto" }}>
              {options.map((opt) => {
                const isSelected = opt === selected;
                return (
                  <button
                    key={opt}
                    onClick={() => { onChange(opt); setOpen(false); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      width: "100%",
                      textAlign: "left",
                      background: isSelected ? `${accentColor}14` : "none",
                      border: "none",
                      borderRadius: 4,
                      padding: "6px 8px",
                      cursor: "pointer",
                      fontSize: FONT_BODY,
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? accentColor : "#323130",
                    }}
                  >
                    <Icon
                      iconName={isSelected ? "RadioBtnOn" : "RadioBtnOff"}
                      styles={{ root: { fontSize: 14, color: isSelected ? accentColor : "#8A8886" } }}
                    />
                    {display(opt)}
                  </button>
                );
              })}
            </div>
          </div>
        </Callout>
      )}
    </>
  );
}

const RiskCell: React.FC<{ level: RiskLevel }> = ({ level }) => {
  const { color, bg, textColor } = RISK_PALETTE[level];
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "1px 9px",
      borderRadius: 999,
      backgroundColor: bg,
      color: textColor,
      fontSize: FONT_BODY,
      fontWeight: 700,
      lineHeight: "16px",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: color, display: "inline-block" }} />
      {adoptionLabel(level)}
    </span>
  );
};

// Renders a MoM / WoW change: signed value with a directional caret plus the
// underlying comparison window (start – end) beneath it. Blank or unparseable
// values render as an em dash so they aren't confused with a real 0% change.
const ChangeCell: React.FC<{ value: string; start: string | null; end: string | null }> = ({ value, start, end }) => {
  const raw = (value ?? "").trim();
  const parsed = raw === "" ? NaN : parseFloat(raw);
  const n = Number.isFinite(parsed) ? parsed : null;
  const isPositive = n !== null && n > 0;
  const isNegative = n !== null && n < 0;
  const range = formatDateRange(start, end);
  const display = n === null ? "—" : `${isPositive ? "+" : ""}${n}%`;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, lineHeight: 1.2 }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          fontSize: FONT_BODY,
          fontWeight: 600,
          color: n === null ? "#8A8886" : isPositive ? "#107C41" : isNegative ? "#C4314B" : "#605E5C",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {isPositive && <Icon iconName="CaretSolidUp" styles={{ root: { fontSize: 10 } }} />}
        {isNegative && <Icon iconName="CaretSolidDown" styles={{ root: { fontSize: 10 } }} />}
        {display}
      </span>
      {range ? <span style={{ fontSize: 10, color: "#8A8886", whiteSpace: "nowrap" }}>{range}</span> : null}
    </div>
  );
};

const S500Cell: React.FC<{ isS500: boolean }> = ({ isS500 }) => (
  <span style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "1px 8px",
    borderRadius: 4,
    backgroundColor: isS500 ? "#EFF6FC" : "#F3F2F1",
    color: isS500 ? "#0078D4" : "#605E5C",
    fontSize: FONT_BODY,
    fontWeight: 600,
    border: isS500 ? "1px solid #C7E0F4" : "1px solid #E1DFDD",
    lineHeight: "16px",
  }}>
    <Icon iconName={isS500 ? "Crown" : "Tag"} styles={{ root: { fontSize: 10 } }} />
    {isS500 ? "S500" : "Non-S500"}
  </span>
);

// =============================================================================
// Row mapper
// =============================================================================
interface BaselineSecurityHealthRow {
  id: string;
  tenantId: string;
  tenantName: string;
  customerName: string;
  avatarColor: string;
  isS500: boolean;
  isLiked: boolean;
  riskLevel: RiskLevel;
  overallScorePercent: number;
  overallActiveKPI: number;
  overallMetStandard: number;
  vertical: string;
  industry: string;
  segment: string;
  region: string;
  licenseSku: string;
  topParentId: string;
  engagementCohort: string;
  entraIdLogins: number;
  mom: string;
  momStart: string | null;
  momEnd: string | null;
  wow: string;
  wowStart: string | null;
  wowEnd: string | null;
  // Per-initiative MoM / WoW change values (empty string when the initiative has
  // no trend data). The comparison-window date ranges (momStart/momEnd,
  // wowStart/wowEnd) are shared across initiatives.
  baselineMom: string;
  passkeyMom: string;
  patchingMom: string;
  endpointMom: string;
  protectionMom: string;
  hygieneMom: string;
  dormantAccountMom: string;
  vulnerabilitiesMom: string;
  monitoringMom: string;
  baselineWow: string;
  passkeyWow: string;
  patchingWow: string;
  endpointWow: string;
  protectionWow: string;
  hygieneWow: string;
  dormantAccountWow: string;
  vulnerabilitiesWow: string;
  monitoringWow: string;
  // Per-initiative adoption bands (empty string when the initiative has no data).
  baselineBand: RiskLevel | "";
  passkeyBand: RiskLevel | "";
  patchingBand: RiskLevel | "";
  endpointBand: RiskLevel | "";
  protectionBand: RiskLevel | "";
  hygieneBand: RiskLevel | "";
  dormantAccountBand: RiskLevel | "";
  vulnerabilitiesBand: RiskLevel | "";
  monitoringBand: RiskLevel | "";
}

// Deterministic avatar palette — same seed always picks the same color so the
// grid avatar stays stable across refreshes without the API needing to carry it.
const AVATAR_PALETTE = [
  "#0F6CBD", "#2B88D8", "#107C41", "#5C2E91", "#B3541E",
  "#038387", "#C4314B", "#0B5CAB", "#6B4FBB", "#986F0B",
  "#0078D4", "#6B4226",
];
const pickAvatarColor = (seed: string): string => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
};

const RISK_VALUES = new Set<RiskLevel>(["High", "Moderate", "Low"]);

const normalizeRisk = (s: string): RiskLevel =>
  (RISK_VALUES.has(s as RiskLevel) ? s : "Moderate") as RiskLevel;

// The Kusto function emits adoption bands ("High Adoption" | "Moderate
// Adoption" | "Low Adoption") in the overallRiskStatus field. Internally this 
// component models each tenant by the inverse *risk* band (High risk = Low adoption)
// so the palette, KPI counting, and filtering logic stays intact; display strings 
// are converted back to adoption via adoptionLabel(). This maps an incoming value 
// (adoption band, or a legacy bare risk band) to the internal risk band.
const toRiskBand = (raw: string | null | undefined): RiskLevel | "" => {
  const s = (raw ?? "").trim().toLowerCase();
  if (!s) return "";
  // Adoption bands (current format) — the inverse of the risk band.
  if (s === "high adoption") return "Low";
  if (s === "moderate adoption") return "Moderate";
  if (s === "low adoption") return "High";
  // Legacy bare risk bands.
  if (s === "high") return "High";
  if (s === "moderate") return "Moderate";
  if (s === "low") return "Low";
  return "";
};

const toRow = (t: BaselineSecurityHealthTenantMetrics): BaselineSecurityHealthRow => ({
  id: t.tenantId,
  tenantId: t.tenantId,
  tenantName: t.tenantName,
  customerName: t.customerName ?? "",
  avatarColor: pickAvatarColor(t.tenantId || t.tenantName),
  isS500: t.s500Status === "S500",
  isLiked: false,
  riskLevel: normalizeRisk(toRiskBand(t.overallRiskStatus) || "Moderate"),
  overallScorePercent: t.overallScorePercent ?? 0,
  overallActiveKPI: t.overallActiveKPI ?? 0,
  overallMetStandard: t.overallMetStandard ?? 0,
  vertical: t.vertical,
  industry: t.industry ?? "",
  segment: t.segment,
  region: t.region,
  licenseSku: t.licenseSKU ?? "",
  topParentId: t.topParentId,
  engagementCohort: t.engagementCohort ?? "",
  entraIdLogins: t.entraIdLogins,
  mom: t.mom ?? "",
  momStart: t.monthlyStartDate ?? null,
  momEnd: t.monthlyEndDate ?? null,
  wow: t.wow ?? "",
  wowStart: t.weeklyStartDate ?? null,
  wowEnd: t.weeklyEndDate ?? null,
  baselineMom: t.baselineMOM ?? "",
  passkeyMom: t.passkeyMOM ?? "",
  patchingMom: t.patchingMOM ?? "",
  endpointMom: t.endpointMOM ?? "",
  protectionMom: t.protectionMOM ?? "",
  hygieneMom: t.hygieneMOM ?? "",
  dormantAccountMom: t.dormantAccountMOM ?? "",
  vulnerabilitiesMom: t.vulnerabilitiesMOM ?? "",
  monitoringMom: t.monitoringMOM ?? "",
  baselineWow: t.baselineWOW ?? "",
  passkeyWow: t.passkeyWOW ?? "",
  patchingWow: t.patchingWOW ?? "",
  endpointWow: t.endpointWOW ?? "",
  protectionWow: t.protectionWOW ?? "",
  hygieneWow: t.hygieneWOW ?? "",
  dormantAccountWow: t.dormantAccountWOW ?? "",
  vulnerabilitiesWow: t.vulnerabilitiesWOW ?? "",
  monitoringWow: t.monitoringWOW ?? "",
  baselineBand: toRiskBand(t.baselineRiskStatus),
  passkeyBand: toRiskBand(t.passkeyRiskStatus),
  patchingBand: toRiskBand(t.patchingRiskStatus),
  endpointBand: toRiskBand(t.endpointRiskStatus),
  protectionBand: toRiskBand(t.protectionRiskStatus),
  hygieneBand: toRiskBand(t.hygieneRiskStatus),
  dormantAccountBand: toRiskBand(t.dormantAccountRiskStatus),
  vulnerabilitiesBand: toRiskBand(t.vulnerabilitiesRiskStatus),
  monitoringBand: toRiskBand(t.monitoringRiskStatus),
});

// Resolves the adoption band shown in the single risk column for the currently
// selected initiative. "Overall" uses the tenant's overall band (row.riskLevel).
const bandForInitiative = (row: BaselineSecurityHealthRow, initiative: Initiative): RiskLevel | "" => {
  switch (initiative) {
    case "Baseline": return row.baselineBand;
    case "Passkey": return row.passkeyBand;
    case "Patching": return row.patchingBand;
    case "Endpoint": return row.endpointBand;
    case "Protection": return row.protectionBand;
    case "Hygiene": return row.hygieneBand;
    case "DormantAccount": return row.dormantAccountBand;
    case "Vulnerabilities": return row.vulnerabilitiesBand;
    case "Monitoring": return row.monitoringBand;
    default: return row.riskLevel;
  }
};

// Resolves the MoM change value shown in the trend column for the currently
// selected initiative. "Overall" uses the tenant's overall MoM (row.mom).
const momForInitiative = (row: BaselineSecurityHealthRow, initiative: Initiative): string => {
  switch (initiative) {
    case "Baseline": return row.baselineMom;
    case "Passkey": return row.passkeyMom;
    case "Patching": return row.patchingMom;
    case "Endpoint": return row.endpointMom;
    case "Protection": return row.protectionMom;
    case "Hygiene": return row.hygieneMom;
    case "DormantAccount": return row.dormantAccountMom;
    case "Vulnerabilities": return row.vulnerabilitiesMom;
    case "Monitoring": return row.monitoringMom;
    default: return row.mom;
  }
};

// Resolves the WoW change value shown in the trend column for the currently
// selected initiative. "Overall" uses the tenant's overall WoW (row.wow).
const wowForInitiative = (row: BaselineSecurityHealthRow, initiative: Initiative): string => {
  switch (initiative) {
    case "Baseline": return row.baselineWow;
    case "Passkey": return row.passkeyWow;
    case "Patching": return row.patchingWow;
    case "Endpoint": return row.endpointWow;
    case "Protection": return row.protectionWow;
    case "Hygiene": return row.hygieneWow;
    case "DormantAccount": return row.dormantAccountWow;
    case "Vulnerabilities": return row.vulnerabilitiesWow;
    case "Monitoring": return row.monitoringWow;
    default: return row.wow;
  }
};

// Raw per-initiative adoption band string straight off the tenant-metrics row
// (e.g. "Low Adoption"). Used to detect which initiatives actually carry data
// so the Initiatives dropdown can be built dynamically instead of hardcoded.
const rawInitiativeRiskStatus = (t: BaselineSecurityHealthTenantMetrics, initiative: Initiative): string => {
  switch (initiative) {
    case "Baseline": return t.baselineRiskStatus ?? "";
    case "Passkey": return t.passkeyRiskStatus ?? "";
    case "Patching": return t.patchingRiskStatus ?? "";
    case "Endpoint": return t.endpointRiskStatus ?? "";
    case "Protection": return t.protectionRiskStatus ?? "";
    case "Hygiene": return t.hygieneRiskStatus ?? "";
    case "DormantAccount": return t.dormantAccountRiskStatus ?? "";
    case "Vulnerabilities": return t.vulnerabilitiesRiskStatus ?? "";
    case "Monitoring": return t.monitoringRiskStatus ?? "";
    default: return t.overallRiskStatus ?? "";
  }
};

// =============================================================================
// Container
// =============================================================================
// =============================================================================
// Columns hidden by default in the grid, mapped to their display label. We log
// when a user explicitly turns one of these on to gauge whether it should be
// surfaced in the default view.
const DEFAULT_HIDDEN_COLUMNS: Readonly<Record<string, string>> = {
  industry: "Industry",
  segment: "Segment",
  actionsImplemented: "Actions Implemented",
  licenseSku: "M365 License SKU",
  engagementCohort: "Engagement Cohort",
};

export const BaselineSecurityHealthContainer: React.FC = () => {
  const navigate = useNavigate();
  const [searchFilter, setSearchFilter] = useState("");
  const [riskFilter, setRiskFilter] = useState<RiskFilter>("all");
  const [s500Filter, setS500Filter] = useState<Set<string>>(new Set());
  const [verticalFilter, setVerticalFilter] = useState<Set<string>>(new Set());
  const [regionFilter, setRegionFilter] = useState<Set<string>>(new Set());
  const [licenseFilter, setLicenseFilter] = useState<Set<string>>(new Set());
  const [cohortFilter, setCohortFilter] = useState<Set<string>>(new Set());
  const [likedOnly, setLikedOnly] = useState(false);
  // Which initiative's adoption band the single risk column shows. Default =
  // Overall; the Initiatives pill swaps the column to another initiative.
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative>("Overall");

  // Filters considered "active" for the Clear-all button — non-default value
  // in any filter counts as one. selectedInitiative is included so a full
  // reset returns the grid to the Overall column.
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (searchFilter.trim() !== "") n += 1;
    if (riskFilter !== "all") n += 1;
    if (s500Filter.size > 0) n += 1;
    if (verticalFilter.size > 0) n += 1;
    if (regionFilter.size > 0) n += 1;
    if (licenseFilter.size > 0) n += 1;
    if (cohortFilter.size > 0) n += 1;
    if (likedOnly) n += 1;
    if (selectedInitiative !== "Overall") n += 1;
    return n;
  }, [searchFilter, riskFilter, s500Filter, verticalFilter, regionFilter, licenseFilter, cohortFilter, likedOnly, selectedInitiative]);

  const resetAllFilters = useCallback(() => {
    setSearchFilter("");
    setRiskFilter("all");
    setS500Filter(new Set());
    setVerticalFilter(new Set());
    setRegionFilter(new Set());
    setLicenseFilter(new Set());
    setCohortFilter(new Set());
    setLikedOnly(false);
    setSelectedInitiative("Overall");
    logEvent("Baseline Security Health - Clear All Filters", "Health View Page", "BaselineSecurityHealthContainer", { previousActiveCount: activeFilterCount });
  }, [activeFilterCount]);

  const {
    data: tenantsResponse,
    isLoading: isTenantsLoading,
    isError: isTenantsError,
    error: tenantsError,
    isFetching: isTenantsRefetching,
  } = useGetBaselineSecurityHealthTenantMetricsQuery();
  const {
    isLoading: isRollupLoading,
    isError: isRollupError,
    error: rollupError,
    isFetching: isRollupRefetching,
  } = useGetBaselineSecurityHealthRiskRollupQuery();

  // Base filter payload (dropdown multi-selects only, no overallRiskStatus).
  // Used to fetch the adoption-band DISTRIBUTION for the Low/Moderate/High
  // cards so those keep showing the true split across bands even when a band
  // filter is active — otherwise clicking e.g. "Moderate Adoption" would
  // collapse all three cards to 0.
  const dropdownFilter = useMemo<AIReadyHealthFilter>(() => {
    const fromSet = (s: Set<string>): string => (s.size > 0 ? Array.from(s).join(",") : "AllUp");
    // Grid treats LICENSE_SKU_NONE as "blank licenseSKU"; forward that intent to
    // the server so trend/KPI counts match the grid instead of filtering for the
    // literal string "None".
    const fromLicenseSkuSet = (s: Set<string>): string =>
      s.size > 0
        ? Array.from(s).map((v) => (v === LICENSE_SKU_NONE ? "" : v)).join(",")
        : "AllUp";
    return {
      overallRiskStatus: "AllUp",
      baselineRiskStatus: "AllUp",
      passkeyRiskStatus: "AllUp",
      patchingRiskStatus: "AllUp",
      endpointRiskStatus: "AllUp",
      protectionRiskStatus: "AllUp",
      hygieneRiskStatus: "AllUp",
      dormantAccountRiskStatus: "AllUp",
      vulnerabilitiesRiskStatus: "AllUp",
      monitoringRiskStatus: "AllUp",
      s500Status: fromSet(s500Filter),
      vertical: fromSet(verticalFilter),
      region: fromSet(regionFilter),
      licenseSKU: fromLicenseSkuSet(licenseFilter),
      engagementCohort: fromSet(cohortFilter),
    };
  }, [s500Filter, verticalFilter, regionFilter, licenseFilter, cohortFilter]);

  // Adoption-band filter for the trend chart + summary cards (Total Controls
  // Adopted / Tenants Improved). Apply the clicked band to the initiative that
  // is currently selected so these surfaces stay aligned with the grid.
  const adoptionFilter = useMemo<AIReadyHealthFilter>(
    () => applyInitiativeRiskFilter(
      dropdownFilter,
      selectedInitiative,
      riskFilter === "all" ? "AllUp" : adoptionLabel(riskFilter)
    ),
    [dropdownFilter, riskFilter, selectedInitiative]
  );

  const { data: adoptionTrendData, isFetching: isTrendFetching, error: trendError } = useGetAIReadyHealthTrendQuery(adoptionFilter);
  const shouldFetchCardAdoptionTrend = searchFilter.trim() === "" && !likedOnly;
  const { data: cardAdoptionTrendData, isFetching: isCardAdoptionTrendFetching } = useGetAIReadyHealthTrendQuery(
    dropdownFilter,
    { skip: !shouldFetchCardAdoptionTrend }
  );
  const { data: cardMetrics, isFetching: isCardMetricsFetching } = useGetAIReadyHealthCardMetricsQuery(adoptionFilter);

  // Summarize the trend fetch error (if any) into a short string for the chart's
  // empty-state, so a server / auth / network failure is visible instead of the
  // generic "no data" message.
  const trendErrorMessage = useMemo<string | null>(() => {
    if (!trendError) return null;
    const anyErr = trendError as { status?: number | string; data?: unknown; error?: string };
    if (anyErr?.status !== undefined) {
      const dataStr = typeof anyErr.data === "string"
        ? anyErr.data
        : anyErr.data ? JSON.stringify(anyErr.data).slice(0, 200) : "";
      return `HTTP ${anyErr.status}${dataStr ? ` — ${dataStr}` : ""}`;
    }
    return anyErr?.error ?? "Unknown error";
  }, [trendError]);

  // Trend points for the currently selected initiative line only.
  const trendPointsForInitiative = useMemo(
    () => (adoptionTrendData?.points ?? []).filter((p) => p.initiative === selectedInitiative),
    [adoptionTrendData, selectedInitiative]
  );

  const adoptionCardWoW = useMemo(
    () => shouldFetchCardAdoptionTrend
      ? adoptionWoWFromTrend(cardAdoptionTrendData?.points ?? [], selectedInitiative)
      : {
          low: null,
          lowPercentage: null,
          moderate: null,
          moderatePercentage: null,
          high: null,
          highPercentage: null,
          previousDate: null,
          currentDate: null,
        },
    [shouldFetchCardAdoptionTrend, cardAdoptionTrendData, selectedInitiative]
  );

  const { data: likedCustomersData } = useGetFavorableCustomerByLoginQuery({});
  // Optimistic overrides keyed by tenantId (true = liked, false = unliked) so the
  // heart flips instantly on click, before the API round-trip / refetch completes.
  const [optimisticLikes, setOptimisticLikes] = useState<Record<string, boolean>>({});
  const likedTenantIds = useMemo(() => {
    const set = new Set<string>();
    for (const c of (likedCustomersData ?? []) as Array<{ tenantId?: string }>) {
      if (c.tenantId) set.add(c.tenantId);
    }
    for (const [tid, liked] of Object.entries(optimisticLikes)) {
      if (liked) set.add(tid);
      else set.delete(tid);
    }
    return set;
  }, [likedCustomersData, optimisticLikes]);

  // Once refreshed favorite data agrees with an optimistic override, drop that
  // override so subsequent server refetches can drive the tenant's state again.
  // Reconciling here (instead of clearing on mutation success) avoids a flicker
  // in the window where the mutation resolved but the favorites refetch is still
  // in flight with stale data.
  useEffect(() => {
    if (!likedCustomersData) return;
    setOptimisticLikes((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      const serverSet = new Set(
        (likedCustomersData as Array<{ tenantId?: string }>)
          .map((c) => c.tenantId)
          .filter((id): id is string => Boolean(id))
      );
      let changed = false;
      const next: Record<string, boolean> = {};
      for (const key of keys) {
        if (serverSet.has(key) === prev[key]) {
          changed = true; // server now matches the override → safe to drop it
        } else {
          next[key] = prev[key];
        }
      }
      return changed ? next : prev;
    });
  }, [likedCustomersData]);

  // Like / unlike a customer directly from the grid. Mirrors the customer page:
  // POSTs the favorite flag; the FavoritesCustomer cache tag is invalidated so
  // the heart state refreshes automatically.
  const [updateFavoriteCustomer] = useInsertUpdateFavCustomersMutation();
  const toggleLike = (tenantId: string, currentlyLiked: boolean) => {
    if (!tenantId) return;
    const nextLiked = !currentlyLiked;
    // Update the UI immediately; revert if the API call fails.
    setOptimisticLikes((prev) => ({ ...prev, [tenantId]: nextLiked }));
    logEvent("Baseline Security Health - Toggle Like", "Health View Page", "BaselineSecurityHealthContainer", {
      tenantId,
      liked: String(nextLiked),
    });
    updateFavoriteCustomer({
      loginName: sessionStorage.getItem("loginName") ?? "",
      loginEmail: sessionStorage.getItem("loginUserName") ?? "",
      tenantId,
      isFavourite: Number(nextLiked),
    })
      .unwrap()
      .catch(() => {
        // Only revert if the current override still matches this request's optimistic
        // state; if the user toggled again meanwhile, keep the newer state.
        setOptimisticLikes((prev) => {
          if (prev[tenantId] !== nextLiked) return prev;
          return { ...prev, [tenantId]: currentlyLiked };
        });
      });
  };

  // The Kusto adoption bands are normalized to the internal risk model up front
  // Normalize the overallRiskStatus to internal risk band value
  // so every downstream memo (counts, filters, rows) sees a consistent value.
  const effectiveTenantsResponse = useMemo(() => {
    if (!tenantsResponse) return tenantsResponse;
    return {
      ...tenantsResponse,
      tenants: (tenantsResponse.tenants ?? []).map((t) => ({
        ...t,
        overallRiskStatus: toRiskBand(t.overallRiskStatus) || "Moderate",
      })),
    };
  }, [tenantsResponse]);

  // Show loading only while fetching
  const isLoading = isTenantsLoading || isRollupLoading;
  const isRefreshing = isTenantsRefetching || isRollupRefetching;
  const isError = isTenantsError || isRollupError;

  // Initiatives available in the Initiatives dropdown are derived from the data:
  // an initiative is offered when at least one loaded tenant carries a non-empty
  // adoption band for it. This mirrors the customer detail page, where empty
  // categories (e.g. Baseline) are not shown, and avoids a hardcoded visible set.
  //
  const availableInitiatives = useMemo<readonly Initiative[]>(() => {
    const tenants = effectiveTenantsResponse?.tenants ?? [];
    return INITIATIVES.filter((ini) => {
      if (ini === "Overall") return true;
      return tenants.some((t) => rawInitiativeRiskStatus(t, ini).trim().length > 0);
    });
  }, [effectiveTenantsResponse]);

  useEffect(() => {
    logEvent("BaselineSecurityHealthTab", "Health View Page", "BaselineSecurityHealthContainer");
  }, []);

  // If the currently selected initiative is no longer present in the data
  // (e.g. after a data refresh removes a category), fall back to Overall.
  useEffect(() => {
    if (!availableInitiatives.includes(selectedInitiative)) {
      setSelectedInitiative("Overall");
    }
  }, [availableInitiatives, selectedInitiative]);

  useEffect(() => {
    if (isError) {
      logException(
        "Health View Page - Baseline Security Health Tab",
        "BaselineSecurityHealthContainer",
        "getBaselineSecurityHealthTenantMetrics/getBaselineSecurityHealthRiskRollup",
        tenantsError ?? rollupError
      );
    }
  }, [isError, tenantsError, rollupError]);

  // Tenants filtered by every active filter EXCEPT the risk filter. Drives both
  // the KPI card counts (so the cards react to the filters) and the grid rows.
  const baseFilteredTenants = useMemo(() => {
    const source = effectiveTenantsResponse?.tenants ?? [];
    const q = searchFilter.trim().toLowerCase();
    return source.filter((t) => {
      if (q) {
        const haystacks = [
          t.tenantName,
          t.tenantId,
          t.topParentId,
        ];
        if (!haystacks.some((h) => (h ?? "").toLowerCase().includes(q))) return false;
      }
      if (s500Filter.size > 0) {
        const label = t.s500Status === "S500" ? "S500" : "Non-S500";
        if (!s500Filter.has(label)) return false;
      }
      if (verticalFilter.size > 0 && !verticalFilter.has(t.vertical)) return false;
      if (regionFilter.size > 0 && !regionFilter.has(t.region)) return false;
      if (licenseFilter.size > 0) {
        const sku = t.licenseSKU?.trim() || "";
        if (!licenseFilter.has(sku) && !(licenseFilter.has(LICENSE_SKU_NONE) && !sku)) return false;
      }
      if (!matchesEngagementCohort(t.engagementCohort, cohortFilter)) return false;
      if (likedOnly && !likedTenantIds.has(t.tenantId)) return false;
      return true;
    });
  }, [effectiveTenantsResponse, searchFilter, s500Filter, verticalFilter, regionFilter, licenseFilter, cohortFilter, likedOnly, likedTenantIds]);

  // The adoption cards and grid must share one tenant population. Tenants with
  // no band for the selected initiative cannot belong to any of the three cards,
  // so omit them from the table as well.
  const initiativeScopedTenants = useMemo(
    () => baseFilteredTenants.filter((t) => toRiskBand(rawInitiativeRiskStatus(t, selectedInitiative)) !== ""),
    [baseFilteredTenants, selectedInitiative]
  );

  const adoptionDistribution = useMemo(
    () => adoptionDistributionFromRiskBands(
      initiativeScopedTenants.map((t) => toRiskBand(rawInitiativeRiskStatus(t, selectedInitiative)) as RiskLevel)
    ),
    [initiativeScopedTenants, selectedInitiative]
  );
  const isAdoptionDistributionFetching = isTenantsLoading || isTenantsRefetching;

  const rows = useMemo<BaselineSecurityHealthRow[]>(() => {
    const q = searchFilter.trim().toLowerCase();
    const filtered = initiativeScopedTenants.filter((t) => {
      const risk = toRiskBand(rawInitiativeRiskStatus(t, selectedInitiative));
      if (riskFilter !== "all" && risk !== riskFilter) return false;
      return true;
    });

    if (q) {
      // Rank matches: tenantName (0) → tenantId (1) → topParentId/TPID (2).
      // Array.prototype.sort is stable, so original order is preserved within each tier.
      const rank = (t: BaselineSecurityHealthTenantMetrics): number => {
        if ((t.tenantName ?? "").toLowerCase().includes(q)) return 0;
        if ((t.tenantId ?? "").toLowerCase().includes(q)) return 1;
        return 2;
      };
      filtered.sort((a, b) => rank(a) - rank(b) || (b.entraIdLogins - a.entraIdLogins));
    } else {
      // Default order: highest EntraID Logins first, always — filters must
      // not change this baseline ordering.
      filtered.sort((a, b) => b.entraIdLogins - a.entraIdLogins);
    }

    return filtered.map((t) => {
      const row = toRow(t);
      row.isLiked = likedTenantIds.has(t.tenantId);
      return row;
    });
  }, [initiativeScopedTenants, searchFilter, riskFilter, likedTenantIds, selectedInitiative]);

  // Derive dropdown option lists from the live data so the filter UI stays
  // in sync with whatever values the Kusto function actually returns.
  const filterOptions = useMemo(() => {
    const verticals = new Set<string>();
    const regions = new Set<string>();
    const licenseSkus = new Set<string>();
    const cohorts = new Set<string>();
    let hasBlankLicense = false;
    for (const t of effectiveTenantsResponse?.tenants ?? []) {
      if (t.vertical) verticals.add(t.vertical);
      if (t.region) regions.add(t.region);
      const sku = t.licenseSKU?.trim();
      if (sku) licenseSkus.add(sku);
      else hasBlankLicense = true;
      for (const cohort of parseEngagementCohorts(t.engagementCohort)) cohorts.add(cohort);
    }
    const sortAsc = (a: string, b: string) => a.localeCompare(b);
    const licenseSkuOptions = Array.from(licenseSkus).sort(sortAsc);
    if (hasBlankLicense) licenseSkuOptions.push(LICENSE_SKU_NONE);
    return {
      verticals: Array.from(verticals).sort(sortAsc),
      regions: Array.from(regions).sort(sortAsc),
      licenseSkus: licenseSkuOptions,
      cohorts: Array.from(cohorts).sort(sortAsc),
    };
  }, [effectiveTenantsResponse]);

  const openTenant = (tenantId: string) => {
    logEvent("Baseline Security Health - Tenant Drilldown", "Health View Page", "BaselineSecurityHealthContainer", {
      tenantId,
    });
    navigate(`/customer?id=${tenantId}&tab=tenantHealthAssessment&from=tenanthealthassessment`);
  };

  // Tracks the current visibility of each default-hidden column so we only log
  // the transition from hidden → visible (i.e. a deliberate opt-in), not the
  // reverse toggle or unrelated column changes.
  const hiddenColumnVisibilityRef = useRef<Record<string, boolean>>(
    Object.fromEntries(Object.keys(DEFAULT_HIDDEN_COLUMNS).map((field) => [field, false]))
  );

  const handleColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    for (const field of Object.keys(DEFAULT_HIDDEN_COLUMNS)) {
      // A column is visible unless the model has an explicit `false` for it.
      const isVisible = model[field] !== false;
      const wasVisible = hiddenColumnVisibilityRef.current[field];
      if (isVisible && !wasVisible) {
        logEvent(
          "Baseline Security Health - Hidden Column Enabled",
          "Health View Page",
          "BaselineSecurityHealthContainer",
          { column: DEFAULT_HIDDEN_COLUMNS[field], columnField: field }
        );
      }
      hiddenColumnVisibilityRef.current[field] = isVisible;
    }
  };

  // MoM/WoW tooltip text is derived from the selected initiative so the header
  // description matches the initiative-specific values shown in the cells. For
  // "Overall" it names the underlying OverallMetStandard metric; for a specific
  // initiative it refers to that initiative's KPIs meeting standard.
  const getTrendTooltip = (trend: "MoM" | "WoW", initiative: Initiative): string => {
    const periodLabel = trend === "MoM" ? "Month-over-Month" : "Week-over-Week";
    const approxWindow = trend === "MoM" ? "~30 days ago" : "~7 days ago";
    const priorPeriod = trend === "MoM" ? "prior month" : "past week";
    const scope = initiative === "Overall"
      ? "KPIs meeting standard (OverallMetStandard)"
      : `${INITIATIVE_LABEL[initiative]} KPIs meeting standard`;
    return `${trend} (${periodLabel}): Change in the number of ${scope} vs ${approxWindow} - latest snapshot minus the nearest snapshot from the ${priorPeriod}. Shown as +N (improved) or -N (regressed); blank if no ${priorPeriod}'s data.`;
  };

  const renderTrendHeader = (label: "MoM" | "WoW", tooltip: string) => (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
      <span>{label}</span>
      <TooltipHost content={tooltip} directionalHint={DirectionalHint.bottomCenter} styles={{ root: { display: "inline-flex" } }}>
        <span
          aria-label={`${label} info`}
          style={{
            width: 18,
            height: 18,
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "transparent",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.7)",
            cursor: "help",
          }}
        >
          <Icon iconName="Info" styles={{ root: { fontSize: 11, color: "#FFFFFF", lineHeight: 1 } }} />
        </span>
      </TooltipHost>
    </Stack>
  );

  // MoM / WoW trend columns are shown for every initiative. When an initiative
  // other than "Overall" is selected the columns surface that initiative's
  // specific MoM/WoW change (the comparison-window date ranges are shared).
  const trendColumns: GridColDef[] = [
    {
      field: "mom",
      headerName: "MoM",
      renderHeader: () => renderTrendHeader("MoM", getTrendTooltip("MoM", selectedInitiative)),
      width: 150,
      sortable: true,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row: BaselineSecurityHealthRow) => parseChange(momForInitiative(row, selectedInitiative)),
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => (
        <ChangeCell value={momForInitiative(p.row, selectedInitiative)} start={p.row.momStart} end={p.row.momEnd} />
      ),
    },
    {
      field: "wow",
      headerName: "WoW",
      renderHeader: () => renderTrendHeader("WoW", getTrendTooltip("WoW", selectedInitiative)),
      width: 150,
      sortable: true,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row: BaselineSecurityHealthRow) => parseChange(wowForInitiative(row, selectedInitiative)),
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => (
        <ChangeCell value={wowForInitiative(p.row, selectedInitiative)} start={p.row.wowStart} end={p.row.wowEnd} />
      ),
    },
  ];

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "tenantName",
      headerName: "Tenant",
      flex: 1.25,
      minWidth: 260,
      sortable: true,
      valueGetter: (_v, row: BaselineSecurityHealthRow) => row.tenantName,
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} styles={{ root: { height: "100%", overflow: "hidden" } }}>
          <TooltipHost content={p.row.isLiked ? "Unlike customer" : "Like customer"} directionalHint={DirectionalHint.topCenter}>
            <Icon
              iconName={p.row.isLiked ? "HeartFill" : "Heart"}
              role="button"
              tabIndex={0}
              aria-label={p.row.isLiked ? "Unlike customer" : "Like customer"}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleLike(p.row.tenantId, p.row.isLiked); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); toggleLike(p.row.tenantId, p.row.isLiked); } }}
              styles={{ root: { fontSize: 14, color: p.row.isLiked ? "#E0245E" : "#8A8886", flexShrink: 0, cursor: "pointer" } }}
            />
          </TooltipHost>
          <div
            style={{
              width: 30, height: 30, borderRadius: 6,
              backgroundColor: p.row.avatarColor, color: "#FFFFFF",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 700, flexShrink: 0,
              letterSpacing: "0.02em",
            }}
          >
            {getCustomerInitials(p.row.tenantName)}
          </div>
          <Stack tokens={{ childrenGap: 2 }} styles={{ root: { minWidth: 0, overflow: "hidden" } }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: { minWidth: 0 } }}>
              <a
                href={`/customer?id=${p.row.tenantId}&tab=tenantHealthAssessment&from=tenanthealthassessment`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); openTenant(p.row.tenantId); }}
                title={p.row.tenantName}
                style={{
                  color: "#0078D4", fontWeight: 600, textDecoration: "none",
                  fontSize: FONT_BODY, lineHeight: 1.2,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
              >
                {p.row.tenantName}
              </a>
            </Stack>
            {p.row.tenantId ? (
              <span
                title={p.row.tenantId}
                style={{
                  fontSize: 10, color: "#5A5A72", lineHeight: 1.2,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}
              >
                {p.row.tenantId}
              </span>
            ) : null}
          </Stack>
        </Stack>
      ),
    },
    {
      field: "customerName",
      headerName: "Customer",
      flex: 1,
      minWidth: 180,
      sortable: true,
      valueGetter: (_v, row: BaselineSecurityHealthRow) => row.customerName,
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => (
        <Stack tokens={{ childrenGap: 2 }} styles={{ root: { minWidth: 0, overflow: "hidden" } }}>
          <span
            title={p.row.customerName}
            style={{
              fontSize: FONT_BODY, color: "#000", fontWeight: 600, lineHeight: 1.2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            {p.row.customerName || "—"}
          </span>
          {p.row.topParentId ? (
            <span
              title={p.row.topParentId}
              style={{
                fontSize: 10, color: "#5A5A72", lineHeight: 1.2,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >
              TPID: {p.row.topParentId}
            </span>
          ) : null}
        </Stack>
      ),
    },
    {
      field: "isS500",
      headerName: "S500",
      width: 100,
      sortable: true,
      type: "boolean",
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => <S500Cell isS500={p.row.isS500} />,
    },
    {
      field: "riskLevel",
      headerName: INITIATIVE_COLUMN_HEADER[selectedInitiative],
      width: 190,
      sortable: true,
      valueGetter: (_v, row: BaselineSecurityHealthRow) => adoptionSortValue(bandForInitiative(row, selectedInitiative)),
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => {
        const band = bandForInitiative(p.row, selectedInitiative);
        return band
          ? <RiskCell level={band} />
          : <span style={{ fontSize: FONT_BODY, color: "#8A8886" }}>—</span>;
      },
    },
    ...trendColumns,
    {
      field: "actionsImplemented",
      headerName: "Actions Implemented",
      width: 150,
      sortable: true,
      valueGetter: (_v, row: BaselineSecurityHealthRow) => row.overallMetStandard,
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => {
        const met = p.row.overallMetStandard;
        const total = p.row.overallActiveKPI;
        const pct = total > 0 ? Math.round((met / total) * 100) : 0;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E", whiteSpace: "nowrap" }}>
              {met} <span style={{ fontWeight: 400, color: "#8A8886", fontSize: 11 }}>of {total}</span>
            </span>
            <div style={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              backgroundColor: "#E5E7EB",
              minWidth: 40,
            }}>
              <div style={{
                height: 4,
                width: `${pct}%`,
                minWidth: pct > 0 ? 2 : 0,
                borderRadius: 2,
                backgroundColor: "#22C55E",
              }} />
            </div>
          </div>
        );
      },
    },
    {
      field: "vertical",
      headerName: "Vertical",
      width: 180,
      sortable: true,
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => (
        <span style={{ fontSize: FONT_BODY, color: "#1A1A2E" }}>{p.row.vertical}</span>
      ),
    },
    {
      field: "industry",
      headerName: "Industry",
      width: 180,
      sortable: true,
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => (
        <span style={{ fontSize: FONT_BODY, color: "#1A1A2E" }}>{p.row.industry}</span>
      ),
    },
    {
      field: "segment",
      headerName: "Segment",
      width: 190,
      sortable: true,
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "1px 8px",
            borderRadius: 4,
            backgroundColor: "#EFF6FC",
            color: "#0F6CBD",
            border: "1px solid #C7E0F4",
            fontSize: FONT_BODY,
            fontWeight: 700,
            letterSpacing: "0.03em",
            lineHeight: "16px",
          }}
        >
          {p.row.segment}
        </span>
      ),
    },
    {
      field: "region",
      headerName: "Region",
      width: 130,
      sortable: true,
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
          <Icon iconName="Globe" styles={{ root: { fontSize: 11, color: "#5A5A72" } }} />
          <span style={{ fontSize: FONT_BODY, color: "#1A1A2E" }}>{p.row.region}</span>
        </Stack>
      ),
    },
    {
      field: "licenseSku",
      headerName: "M365 License SKU",
      width: 150,
      sortable: true,
      valueGetter: (_v, row: BaselineSecurityHealthRow) => row.licenseSku || "",
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "2px 10px",
            borderRadius: 4,
            backgroundColor: p.row.licenseSku ? "#F3E8FF" : "#F3F2F1",
            color: p.row.licenseSku ? "#5C2E91" : "#8A8886",
            border: p.row.licenseSku ? "1px solid #D4B5F5" : "1px solid #E1DFDD",
            fontSize: FONT_BODY,
            fontWeight: 600,
            lineHeight: "18px",
          }}
        >
          {p.row.licenseSku || "—"}
        </span>
      ),
    },
    {
      field: "engagementCohort",
      headerName: "Engagement Cohort",
      width: 180,
      sortable: true,
      valueGetter: (_v, row: BaselineSecurityHealthRow) => row.engagementCohort || "",
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "2px 10px",
            borderRadius: 4,
            backgroundColor: p.row.engagementCohort ? "#FFF3E8" : "#F3F2F1",
            color: p.row.engagementCohort ? "#B3541E" : "#8A8886",
            border: p.row.engagementCohort ? "1px solid #F5D4B5" : "1px solid #E1DFDD",
            fontSize: FONT_BODY,
            fontWeight: 600,
            lineHeight: "18px",
            maxWidth: "160px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={p.row.engagementCohort}
        >
          {p.row.engagementCohort || "—"}
        </span>
      ),
    },
    {
      field: "entraIdLogins",
      headerName: "EntraID Logins",
      width: 140,
      sortable: true,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: (p: GridRenderCellParams<BaselineSecurityHealthRow>) => (
        <span
          style={{
            fontSize: FONT_BODY,
            fontWeight: 600,
            color: "#1A1A2E",
            fontFamily: "Roboto, 'Segoe UI', sans-serif",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {p.row.entraIdLogins.toLocaleString()}
        </span>
      ),
    },
  ], [selectedInitiative, trendColumns]);

  const labelRoot: IRawStyle = {
    fontSize: FONT_LABEL,
    fontWeight: 700,
    color: "#605E5C",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };
  const labelStyles: ITextStyles = { root: labelRoot };

  return (
    <Stack tokens={{ childrenGap: 14 }} styles={{ root: { padding: "12px 8px 32px", position: "relative" } }}>

      {/* Background refresh indicator */}
      {isRefreshing && (
        <div style={{
          position: "fixed",
          top: 60,
          right: 20,
          zIndex: 1000,
          backgroundColor: "#0078D4",
          color: "white",
          padding: "8px 16px",
          borderRadius: "4px",
          fontSize: "12px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
        }}>
          <Spinner size={SpinnerSize.xSmall} styles={{ circle: { borderColor: "white", borderTopColor: "transparent" } }} />
          Refreshing data...
        </div>
      )}

      {/* Scope info banner shown under the page breadcrumb / tab strip */}
      <div
        role="note"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          backgroundColor: "#FFF8E5",
          border: "1px solid #F4E2A3",
          borderLeft: "3px solid #E8B900",
          borderRadius: 6,
          padding: "8px 12px",
          color: "#1A1A2E",
          fontSize: FONT_BODY,
        }}
      >
        <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#8A6D00" } }} />
        <span>
          This includes FastTrack customers and customers enrolled in the Getting Customer Ready for AI (GCR-AI) initiative.
        </span>
      </div>

      {isError ? (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
          Unable to load Ready for AI data. Please retry or contact support if the issue persists.
        </MessageBar>
      ) : null}

      {isLoading && !effectiveTenantsResponse ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            backgroundColor: "#FAFAFA",
            borderRadius: 12,
            border: "1px solid #EDEBE9",
            boxShadow: "0 2px 8px rgba(16,24,40,.06)",
          }}
        >
          <Spinner size={SpinnerSize.large} styles={{ circle: { width: 48, height: 48 } }} />
          <div style={{ marginTop: 20, fontSize: 16, fontWeight: 600, color: "#323130" }}>
            Loading Security Health Data
          </div>
          <div style={{ marginTop: 6, fontSize: 13, color: "#605E5C" }}>
            Please wait while we fetch the latest tenant metrics...
          </div>
        </div>
      ) : (
        <>
          {/* ── Filter card ── */}
          <div
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #EDEBE9",
              borderRadius: 8,
              padding: "12px 18px",
              boxShadow: "0 1px 3px rgba(16,24,40,.06)",
            }}
          >
            <Stack tokens={{ childrenGap: 14 }}>
              {/* Centered search bar — no label, constrained width */}
              <Stack horizontalAlign="center" styles={{ root: { width: "100%" } }}>
                <div style={{ width: "100%", maxWidth: 880 }}>
                  <SearchBox
                    placeholder="Search by tenant name, tenant ID, or TPID..."
                    value={searchFilter}
                    onChange={(_, v) => setSearchFilter(v ?? "")}
                    onClear={() => setSearchFilter("")}
                  />
                </div>
              </Stack>

              {/* Filters label inline with the pills row */}
              <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} styles={{ root: { flexWrap: "wrap" } }}>
                <Text styles={{ root: { ...labelRoot, minWidth: 60 } }}>Filters</Text>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} wrap styles={{ root: { flex: 1, minWidth: 0 } }}>
                  {/* Security Health group — tinted pill cluster with leading label */}
                  <Stack
                    horizontal
                    verticalAlign="center"
                    tokens={{ childrenGap: 8 }}
                    styles={{
                      root: {
                        backgroundColor: "#F0F6FC",
                        border: "1px solid #C7E0F4",
                        borderRadius: 999,
                        padding: "3px 10px 3px 12px",
                        flexShrink: 0,
                      },
                    }}
                  >
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                      <Icon iconName="Encryption" styles={{ root: { fontSize: 12, color: "#0078D4" } }} />
                      <span style={{ fontSize: FONT_LABEL, fontWeight: 700, color: "#0F6CBD", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                        Security Health
                      </span>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 4 }}>
                      <FilterPill label="All" active={riskFilter === "all"} onClick={() => setRiskFilter("all")} />
                      <FilterPill label="High Adoption" active={riskFilter === "Low"} activeColor="#73AA24" icon="SkypeCircleCheck" onClick={() => setRiskFilter("Low")} />
                      <FilterPill label="Moderate Adoption" active={riskFilter === "Moderate"} activeColor="#D0B232" icon="WarningSolid" onClick={() => setRiskFilter("Moderate")} />
                      <FilterPill label="Low Adoption" active={riskFilter === "High"} activeColor="#D77440" icon="AlertSolid" onClick={() => setRiskFilter("High")} />
                    </Stack>
                  </Stack>

                  {/* Liked customers toggle — restricts the grid to the user's favorited tenants */}
                  <FilterPill
                    label="Liked Customers"
                    active={likedOnly}
                    activeColor="#E0245E"
                    icon="HeartFill"
                    onClick={() => setLikedOnly((v) => !v)}
                  />

                  {/* Vertical divider between the two filter groups */}
                  <span style={{ display: "inline-block", width: 1, height: 28, backgroundColor: "#E1DFDD" }} aria-hidden />

                  {/* Dropdown filter pills */}
                  <Stack horizontal tokens={{ childrenGap: 8 }} wrap>
                    {/* Initiatives — single-select; swaps the single adoption column shown
                        in the grid (Overall is the default). */}
                    <SingleSelectFilterPill<Initiative>
                      label="Initiatives"
                      options={availableInitiatives}
                      selected={selectedInitiative}
                      defaultValue="Overall"
                      accentColor="#5C2E91"
                      formatOption={(ini) => INITIATIVE_LABEL[ini]}
                      onChange={(ini) => {
                        setSelectedInitiative(ini);
                        logEvent("Baseline Security Health - Initiative Selected", "Health View Page", "BaselineSecurityHealthContainer", { initiative: ini });
                      }}
                    />
                    <DropdownFilterPill label="S500 Status" options={S500_OPTIONS} selected={s500Filter} onChange={setS500Filter} accentColor="#0078D4" />
                    <DropdownFilterPill label="Vertical" options={filterOptions.verticals} selected={verticalFilter} onChange={setVerticalFilter} accentColor="#5C2E91" />
                    <DropdownFilterPill label="Region" options={filterOptions.regions} selected={regionFilter} onChange={setRegionFilter} accentColor="#038387" />
                    <DropdownFilterPill label="M365 License SKU" options={filterOptions.licenseSkus} selected={licenseFilter} onChange={setLicenseFilter} accentColor="#5C2E91" formatOption={formatLicenseSku} tooltip={M365_LICENSE_SKU_TOOLTIP} />
                    <DropdownFilterPill label="Engagement Cohort" options={filterOptions.cohorts} selected={cohortFilter} onChange={setCohortFilter} accentColor="#B3541E" />
                    <ClearFiltersButton
                      activeCount={activeFilterCount}
                      onClick={resetAllFilters}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </div>

          {/* ── Adoption trend (weekly time-trend of the three adoption bands for the selected initiative) ── */}
          <AdoptionTrendChart
            points={trendPointsForInitiative}
            initiativeLabel={INITIATIVE_LABEL[selectedInitiative]}
            isLoading={isTrendFetching}
            error={trendErrorMessage}
            totalPointsFetched={adoptionTrendData?.points?.length ?? 0}
          />

          {/* ── KPI cards ── Adoption band cards use the band-distribution
              payload (dropdown filters only) so all three keep showing the
              real split. Summary cards + chart follow the clicked band. ── */}
          <Stack horizontal tokens={{ childrenGap: 14 }} styles={{ root: { flexWrap: "wrap" } }}>
            <SummaryKpiCard
              label="Low Adoption"
              pct={adoptionDistribution.totalTenants > 0 ? Math.round((adoptionDistribution.low / adoptionDistribution.totalTenants) * 100) : 0}
              count={adoptionDistribution.low}
              total={adoptionDistribution.totalTenants}
              wow={adoptionCardWoW.low}
              wowPercentage={adoptionCardWoW.lowPercentage}
              wowStart={adoptionCardWoW.previousDate}
              wowEnd={adoptionCardWoW.currentDate}
              riskLevel="High"
              active={riskFilter === "High"}
              isLoading={isAdoptionDistributionFetching || isCardAdoptionTrendFetching}
              onClick={() => setRiskFilter((p) => (p === "High" ? "all" : "High"))}
            />
            <SummaryKpiCard
              label="Moderate Adoption"
              pct={adoptionDistribution.totalTenants > 0 ? Math.round((adoptionDistribution.moderate / adoptionDistribution.totalTenants) * 100) : 0}
              count={adoptionDistribution.moderate}
              total={adoptionDistribution.totalTenants}
              wow={adoptionCardWoW.moderate}
              wowPercentage={adoptionCardWoW.moderatePercentage}
              wowStart={adoptionCardWoW.previousDate}
              wowEnd={adoptionCardWoW.currentDate}
              riskLevel="Moderate"
              active={riskFilter === "Moderate"}
              isLoading={isAdoptionDistributionFetching || isCardAdoptionTrendFetching}
              onClick={() => setRiskFilter((p) => (p === "Moderate" ? "all" : "Moderate"))}
            />
            <SummaryKpiCard
              label="High Adoption"
              pct={adoptionDistribution.totalTenants > 0 ? Math.round((adoptionDistribution.high / adoptionDistribution.totalTenants) * 100) : 0}
              count={adoptionDistribution.high}
              total={adoptionDistribution.totalTenants}
              wow={adoptionCardWoW.high}
              wowPercentage={adoptionCardWoW.highPercentage}
              wowStart={adoptionCardWoW.previousDate}
              wowEnd={adoptionCardWoW.currentDate}
              riskLevel="Low"
              active={riskFilter === "Low"}
              isLoading={isAdoptionDistributionFetching || isCardAdoptionTrendFetching}
              onClick={() => setRiskFilter((p) => (p === "Low" ? "all" : "Low"))}
            />
            <TotalControlsAdoptedCard
              adopted={cardMetrics?.totalControlsAdopted ?? 0}
              total={cardMetrics?.totalControls ?? 0}
              avgPerTenant={cardMetrics?.avgControlsPerTenant ?? 0}
              wow={cardMetrics?.totalControlsAdoptedWoW ?? null}
              wowPercentage={cardMetrics?.totalControlsAdoptedWoWPercentage ?? null}
              cumulativeStartDate={cardMetrics?.cumulativeStartDate ?? null}
              wowStart={cardMetrics?.prevDate ?? null}
              wowEnd={cardMetrics?.currDate ?? null}
              isLoading={isCardMetricsFetching}
            />
            <TenantsImprovedCard
              cumulativeImproved={cardMetrics?.cumulativeImprovedTenants ?? null}
              improvedThisWeek={cardMetrics?.tenantsImprovedThisWeek ?? cardMetrics?.tenantsImprovedAdoption ?? 0}
              improvedPreviousWeek={cardMetrics?.tenantsImprovedPreviousWeek ?? 0}
              improvedWoW={cardMetrics?.tenantsImprovedWoW ?? null}
              improvedWoWPercentage={cardMetrics?.tenantsImprovedWoWPercentage ?? null}
              total={adoptionDistribution.totalTenants}
              cumulativeStartDate={cardMetrics?.cumulativeStartDate ?? null}
              weekStartDate={cardMetrics?.prevDate ?? null}
              weekEndDate={cardMetrics?.currDate ?? null}
              isLoading={isCardMetricsFetching}
            />
          </Stack>

          {/* ── Data Grid (shared Security360 CustomDataGrid — blue header, sort icons, built-in toolbar) ── */}
          <div
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E1E5EB",
              borderRadius: 8,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(16,24,40,.06)",
            }}
          >
            <CustomDataGrid
              autoHeight
              columns={columns}
              rows={{ values: rows, count: rows.length }}
              getRowId={(row) => row.id}
              density="compact"
              rowHeight={56}
              disableRowSelectionOnClick
              onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 25 } },
                sorting: { sortModel: [{ field: "entraIdLogins", sort: "desc" }] },
                columns: {
                  columnVisibilityModel: {
                    industry: false,
                    segment: false,
                    actionsImplemented: false,
                    licenseSku: false,
                    engagementCohort: false,
                    mom: false,
                    wow: false,
                  },
                },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              showExploreDataButtonOnFooter={false}
              exportCsvButtonProps={{ fileNamePrefix: "good-state-health-tenants" }}
              toolbarConfig={{ disableGridToolbarQuickFilter: true }}
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": {
                  fontSize: "12px",
                  fontFamily: '"Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif',
                  color: "#1A1A2E",
                  borderBottom: "1px solid #E1E5EB",
                  padding: "0 10px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
                "& .MuiDataGrid-row": { transition: "background-color 0.12s" },
                "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(0,120,212,0.06)" },
              }}
            />
          </div>
        </>
      )}
    </Stack>
  );
};
